import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaUserGraduate,
  FaChalkboardTeacher,
  FaBuilding,
  FaBullhorn,
  FaUserShield,
  FaUserCircle,
  FaSignOutAlt,
  FaChevronDown,
  FaList,
  FaUserPlus,
  FaUserClock,
  FaBook,
  FaMoneyBillWave,
  FaChartPie,
  FaClipboardList,
  FaFileInvoiceDollar,
  FaIdCard,
  FaFilePrescription,
  FaImages,
  FaCalendarAlt,
  FaGraduationCap,
  FaAward,
  FaReceipt,
  FaClock,
  FaLayerGroup,
  FaSlidersH,
  FaShieldAlt,
  FaLock,
  FaEye,
  FaTimes,
} from "react-icons/fa";
import { MdDashboard } from "react-icons/md";
import { toast } from "react-toastify";
import axios from "axios";
import { server } from "../server";
import useAuth from "../useAuth";
import DeeNovoLogo from "./DeeNovoLogo";

const resolveIcon = (iconName) => {
  if (React.isValidElement(iconName)) return iconName;
  const map = {
    MdDashboard: <MdDashboard />,
    FaGraduationCap: <FaGraduationCap />,
    FaBuilding: <FaBuilding />,
    FaBook: <FaBook />,
    FaClipboardList: <FaClipboardList />,
    FaUserGraduate: <FaUserGraduate />,
    FaList: <FaList />,
    FaUserPlus: <FaUserPlus />,
    FaIdCard: <FaIdCard />,
    FaFilePrescription: <FaFilePrescription />,
    FaChalkboardTeacher: <FaChalkboardTeacher />,
    FaUserClock: <FaUserClock />,
    FaAward: <FaAward />,
    FaBullhorn: <FaBullhorn />,
    FaMoneyBillWave: <FaMoneyBillWave />,
    FaReceipt: <FaReceipt />,
    FaLayerGroup: <FaLayerGroup />,
    FaClock: <FaClock />,
    FaSlidersH: <FaSlidersH />,
    FaShieldAlt: <FaShieldAlt />,
    FaFileInvoiceDollar: <FaFileInvoiceDollar />,
    FaChartPie: <FaChartPie />,
    FaImages: <FaImages />,
    FaCalendarAlt: <FaCalendarAlt />,
    FaUserShield: <FaUserShield />,
    FaUserCircle: <FaUserCircle />,
  };
  return map[iconName] || <FaBuilding />;
};

// Fallback static structure
const staticMenuItems = [
  {
    section: "Overview",
    items: [
      {
        title: "Dashboard",
        code: "DASHBOARD",
        path: "",
        icon: <MdDashboard />,
      },
    ],
  },
  {
    section: "Academics",
    items: [
      {
        title: "Academics",
        code: "ACADEMICS",
        icon: <FaGraduationCap />,
        subItems: [
          {
            title: "Departments",
            code: "DEPARTMENTS",
            path: "departments",
            icon: <FaBuilding />,
          },
          {
            title: "Subjects",
            code: "SUBJECTS",
            path: "subjects",
            icon: <FaBook />,
          },
          {
            title: "Fee Structures",
            code: "FEE_STRUCTURES",
            path: "fee-structures",
            icon: <FaClipboardList />,
          },
        ],
      },
    ],
  },
  {
    section: "Students",
    items: [
      {
        title: "Students",
        code: "STUDENT_MGMT",
        icon: <FaUserGraduate />,
        subItems: [
          {
            title: "Student List",
            code: "STUDENTS_LIST",
            path: "students",
            icon: <FaList />,
          },
          {
            title: "Register Student",
            code: "STUDENT_REGISTRATION",
            path: "students/register",
            icon: <FaUserPlus />,
          },
          {
            title: "Generate ID Card",
            code: "STUDENT_ID_CARD",
            path: "students/id-card",
            icon: <FaIdCard />,
          },
          {
            title: "Issue TC",
            code: "STUDENT_TC",
            path: "students/generate-tc",
            icon: <FaFilePrescription />,
          },
          {
            title: "Issued TC",
            code: "STUDENT_ISSUED_TC",
            path: "students/transfer-certificates",
            icon: <FaClipboardList />,
          },
        ],
      },
    ],
  },
  {
    section: "Employees",
    items: [
      {
        title: "Employees",
        code: "EMPLOYEE_MGMT",
        icon: <FaChalkboardTeacher />,
        subItems: [
          {
            title: "Employees List",
            code: "EMPLOYEES_LIST",
            path: "employees",
            icon: <FaList />,
          },
          {
            title: "Register Employee",
            code: "EMPLOYEE_REGISTRATION",
            path: "employees/register",
            icon: <FaUserPlus />,
          },
          {
            title: "Generate ID Card",
            code: "EMPLOYEE_ID_CARD",
            path: "employees/id-card",
            icon: <FaIdCard />,
          },
          {
            title: "Attendance",
            code: "EMPLOYEE_ATTENDANCE",
            path: "employees/attendance",
            icon: <FaUserClock />,
          },
          {
            title: "New Exp. Cert",
            code: "EMPLOYEE_EXP_CERT_GEN",
            path: "employees/generate-exp-cert",
            icon: <FaAward />,
          },
          {
            title: "Exp. Certs",
            code: "EMPLOYEE_EXP_CERT",
            path: "employees/experience-certificates",
            icon: <FaClipboardList />,
          },
        ],
      },
    ],
  },
  {
    section: "Communication",
    items: [
      {
        title: "Communication",
        code: "COMMUNICATION",
        icon: <FaBullhorn />,
        subItems: [
          {
            title: "Notices",
            code: "NOTICES",
            path: "notices",
            icon: <FaBullhorn />,
          },
        ],
      },
    ],
  },
  {
    section: "Enterprise Fees",
    items: [
      {
        title: "Enterprise Fees",
        code: "FEE_MGMT",
        icon: <FaMoneyBillWave />,
        badge: "ERP",
        subItems: [
          {
            title: "Collect Student Fee",
            code: "FEE_COLLECT",
            path: "fees/collect",
            icon: <FaMoneyBillWave />,
          },
          {
            title: "Fee Receipt Register",
            code: "FEE_RECEIPTS",
            path: "fees/receipts",
            icon: <FaReceipt />,
          },
          {
            title: "Assign Bulk Fees",
            code: "FEE_ASSIGN",
            path: "fees/assign",
            icon: <FaLayerGroup />,
          },
          {
            title: "Upcoming Dues",
            code: "FEE_DEFAULTERS",
            path: "fees/upcoming-dues",
            icon: <FaClock />,
          },
          {
            title: "Fee Structure Master",
            code: "FEE_CATEGORIES",
            path: "fees/categories",
            icon: <FaSlidersH />,
          },
          {
            title: "Fee Reports & Audit",
            code: "FEE_REPORTS",
            path: "fees/reports",
            icon: <FaClipboardList />,
          },
          {
            title: "Global Fee Settings",
            code: "FEE_SETTINGS",
            path: "fees/settings",
            icon: <FaShieldAlt />,
          },
        ],
      },
    ],
  },
  {
    section: "Finance & Accounts",
    items: [
      {
        title: "Finance & Accounts",
        code: "EXPENSES",
        icon: <FaFileInvoiceDollar />,
        subItems: [
          {
            title: "Expenses Overview",
            code: "EXPENSES_OVERVIEW",
            path: "expenses/dashboard",
            icon: <FaChartPie />,
          },
          {
            title: "Expenses Register",
            code: "EXPENSES_REGISTER",
            path: "expenses",
            icon: <FaList />,
          },
        ],
      },
    ],
  },
  {
    section: "School Portal",
    items: [
      {
        title: "School Portal",
        code: "SCHOOL_PORTAL",
        icon: <FaImages />,
        subItems: [
          {
            title: "Gallery",
            code: "GALLERY",
            path: "gallery",
            icon: <FaImages />,
          },
          {
            title: "Events Calendar",
            code: "EVENTS",
            path: "events",
            icon: <FaCalendarAlt />,
          },
        ],
      },
    ],
  },
  {
    section: "Security",
    items: [
      {
        title: "Security",
        code: "SECURITY_MGMT",
        icon: <FaUserShield />,
        subItems: [
          {
            title: "Roles & Access Matrix",
            code: "ROLES_MATRIX",
            path: "roles",
            icon: <FaUserShield />,
          },
          {
            title: "Module Access Mng.",
            code: "MODULE_ACCESS_MGMT",
            path: "modules",
            icon: <FaLayerGroup />,
          },
          {
            title: "User Management",
            code: "USER_ACCOUNTS",
            path: "users",
            icon: <FaUserCircle />,
          },
        ],
      },
    ],
  },
  {
    section: "Account",
    items: [
      {
        title: "My Profile",
        code: "PROFILE",
        path: "profile",
        icon: <FaUserCircle />,
      },
    ],
  },
];

export default function NavBar({ onCloseMobile }) {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [rolePermissions, setRolePermissions] = useState([]);
  const [dynamicSections, setDynamicSections] = useState([]);
  const [loadingModules, setLoadingModules] = useState(true);

  useEffect(() => {
    const fetchPermittedModules = async () => {
      if (!token) return;
      try {
        setLoadingModules(true);
        const [modRes, permRes] = await Promise.all([
          axios.get(`${server}/permissions/my-modules`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${server}/permissions/my-permissions`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if (permRes.data.success) {
          setRolePermissions(permRes.data.data || []);
        }

        if (
          modRes.data.success &&
          Array.isArray(modRes.data.data) &&
          modRes.data.data.length > 0
        ) {
          const transformed = modRes.data.data.map((m) => {
            const hasSub =
              m.items && m.items.length > 0 && m.items[0].path !== "";
            if (hasSub) {
              return {
                section: m.section || m.title,
                items: [
                  {
                    title: m.title,
                    code: m.code,
                    icon: resolveIcon(m.icon),
                    subItems: m.items.map((sub) => ({
                      title: sub.title,
                      code: sub.code,
                      path: sub.path,
                      icon: resolveIcon(sub.icon),
                    })),
                  },
                ],
              };
            } else {
              return {
                section: m.section && m.section !== m.title ? m.section : "",
                items: [
                  {
                    title: m.title,
                    code: m.code,
                    path: m.items?.[0]?.path || "",
                    icon: resolveIcon(m.icon),
                  },
                ],
              };
            }
          });
          setDynamicSections(transformed);
        } else {
          setDynamicSections(staticMenuItems);
        }
      } catch (err) {
        console.error("Failed to load permitted modules:", err);
        setDynamicSections(staticMenuItems);
      } finally {
        setLoadingModules(false);
      }
    };
    fetchPermittedModules();
  }, [token, user]);

  const rawRole =
    typeof user?.role === "object"
      ? user?.role?.name
      : user?.role || user?.roleName || user?.userType || "";
  const userRoleLower = (rawRole || "").toString().toLowerCase();

  const isAdminRole =
    user?.roleId === 1 ||
    user?.role?.id === 1 ||
    userRoleLower === "admin" ||
    userRoleLower === "superadmin" ||
    userRoleLower === "administrator";

  const rawSections =
    dynamicSections.length > 0 ? dynamicSections : staticMenuItems;

  const visibleSections = React.useMemo(() => {
    if (isAdminRole) return rawSections;

    return rawSections
      .map((sec) => {
        const filteredItems = (sec.items || [])
          .map((item) => {
            // Check top-level permission from database
            const itemPerm = rolePermissions.find(
              (p) =>
                (p.code &&
                  item.code &&
                  p.code.toLowerCase() === item.code.toLowerCase()) ||
                (p.routePath &&
                  item.path &&
                  (p.routePath === `/my/${item.path}` ||
                    p.routePath === item.path)) ||
                (p.title &&
                  item.title &&
                  p.title.toLowerCase() === item.title.toLowerCase()),
            );

            // If top level is explicitly disabled (canView === false), filter out
            if (itemPerm && itemPerm.canView === false) {
              return null;
            }

            if (item.subItems && item.subItems.length > 0) {
              const filteredSub = item.subItems.filter((sub) => {
                const subPerm = rolePermissions.find(
                  (p) =>
                    (p.code &&
                      sub.code &&
                      p.code.toLowerCase() === sub.code.toLowerCase()) ||
                    (p.routePath &&
                      sub.path &&
                      (p.routePath === `/my/${sub.path}` ||
                        p.routePath === sub.path)) ||
                    (p.title &&
                      sub.title &&
                      p.title.toLowerCase() === sub.title.toLowerCase()),
                );

                if (subPerm && subPerm.canView === false) return false;

                return true;
              });

              if (filteredSub.length === 0) return null;

              return { ...item, subItems: filteredSub };
            }

            return item;
          })
          .filter(Boolean);

        if (filteredItems.length === 0) return null;

        return { ...sec, items: filteredItems };
      })
      .filter(Boolean);
  }, [rawSections, rolePermissions, isAdminRole]);

  return (
    <aside className="bg-white dark:bg-slate-900 w-full h-full min-h-full border-r border-gray-200 dark:border-slate-800 flex flex-col font-sans transition-colors duration-300 shadow-lg">
      {/* Brand Header */}
      <div className="px-4 py-4 sm:px-5 sm:py-4.5 border-b border-slate-100 dark:border-slate-800/80 flex items-center justify-between bg-gradient-to-r from-slate-50/80 via-white to-sky-50/30 dark:from-slate-900 dark:via-slate-900 dark:to-slate-900/90 backdrop-blur-md">
        <div
          onClick={() => {
            navigate("/my");
            if (onCloseMobile) onCloseMobile();
          }}
          className="flex items-center gap-3.5 cursor-pointer group/brand select-none min-w-0"
          title="Go to Dee Novo Dashboard"
        >
          {/* Logo Card with Ambient Glow */}
          <div className="relative shrink-0">
            <div className="relative -ml-2 flex items-center justify-center w-[60px] h-[60px]overflow-hidden bg-white rounded-md border border-gray-300 p-[0.2px] rounded-r-full">
              <img
                className="w-full h-full rounded-r-full"
                src="/assets/images/logo-icon.jpg"
              />
            </div>
          </div>

          {/* Brand Name & Enterprise ERP Badge */}
          <div className="flex flex-col min-w-0 justify-center">
            <div className="flex items-center gap-1.5">
              <span className="text-[17px] sm:text-[18px] font-black tracking-tight bg-gradient-to-r from-slate-950 via-slate-800 to-sky-900 dark:from-white dark:via-slate-100 dark:to-sky-200 bg-clip-text text-transparent group-hover/brand:from-sky-600 group-hover/brand:to-blue-700 dark:group-hover/brand:from-sky-400 dark:group-hover/brand:to-blue-400 transition-all duration-300">
                Dee Novo School
              </span>
            </div>
            <div className="flex items-center gap-1 mt-0.5">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider bg-sky-50 dark:bg-sky-950/60 text-sky-700 dark:text-sky-300 border border-sky-200/70 dark:border-sky-800/60 shadow-2xs">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                </span>
                Admin Panel
              </span>
            </div>
          </div>
        </div>

        {onCloseMobile && (
          <button
            onClick={onCloseMobile}
            className="lg:hidden p-2 text-slate-400 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label="Close Sidebar"
          >
            <FaTimes size={18} />
          </button>
        )}
      </div>

      {/* Navigation List */}
      <div className="flex-1 overflow-y-auto py-5 px-3 space-y-6 custom-scrollbar">
        {loadingModules ? (
          <div className="py-4 px-2 space-y-4">
            <div className="flex items-center gap-2 text-xs font-bold text-sky-600 dark:text-sky-400 px-2">
              <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-sky-600 border-t-transparent" />
              <span>Loading authorized workspace...</span>
            </div>
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="h-10 bg-slate-100 dark:bg-slate-800/60 rounded-xl animate-pulse"
              />
            ))}
          </div>
        ) : visibleSections.length === 0 ? (
          <div className="py-12 px-4 text-center text-xs font-bold text-gray-400 dark:text-slate-500 italic">
            No accessible modules assigned to your role.
          </div>
        ) : (
          <AnimatePresence>
            {visibleSections.map((section, idx) => {
              const firstItemTitle = section.items?.[0]?.title || "";
              const showSectionHeader =
                Boolean(section.section) &&
                !(
                  section.items?.length === 1 &&
                  !section.items[0]?.subItems &&
                  section.section.trim().toLowerCase() ===
                    firstItemTitle.trim().toLowerCase()
                );

              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: idx * 0.04 }}
                >
                  {showSectionHeader && (
                    <h3 className="px-3 mb-2 text-[10px] font-black uppercase text-gray-400 dark:text-slate-500 tracking-widest">
                      {section.section}
                    </h3>
                  )}
                  <div className="space-y-1">
                    {section.items.map((item, index) => (
                      <NavItem
                        key={index}
                        item={item}
                        currentPath={location.pathname}
                        navigate={navigate}
                        userRole={
                          typeof user?.role === "object"
                            ? user?.role?.name
                            : user?.role ||
                              user?.roleName ||
                              user?.userType ||
                              ""
                        }
                        rolePermissions={rolePermissions}
                        onCloseMobile={onCloseMobile}
                      />
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </div>

      {/* Profile & Logout Footer */}
      <div className="p-4 border-t border-gray-100 dark:border-slate-800 bg-gray-50/50 dark:bg-slate-900/50 mt-auto">
        <div className="flex items-center justify-between p-2.5 mb-2 rounded-2xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700/60 shadow-xs">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-sky-500 to-blue-600 text-white font-black text-xs flex items-center justify-center shadow-xs">
              {(user?.username || "A").charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="text-xs font-black text-gray-900 dark:text-white truncate max-w-[110px]">
                {user?.username || "Admin"}
              </div>
              <div className="text-[10px] font-bold text-sky-600 dark:text-sky-400 capitalize">
                {user?.role?.name || "Super Admin"}
              </div>
            </div>
          </div>

          <button
            onClick={logout}
            className="p-2 text-rose-500 hover:text-rose-700 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-xl transition"
            title="Logout"
          >
            <FaSignOutAlt size={14} />
          </button>
        </div>

        <div className="text-[10px] text-center text-gray-400 dark:text-slate-500 font-bold">
          © 2026 Dee Novo School • v2.0
        </div>
      </div>
    </aside>
  );
}

const NavItem = ({
  item,
  currentPath,
  navigate,
  userRole,
  rolePermissions,
  onCloseMobile,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const rawRole = typeof userRole === "object" ? userRole?.name : userRole;
  const userRoleLower = (rawRole || "").toString().toLowerCase();

  const isAdminRole =
    userRoleLower === "admin" ||
    userRoleLower === "superadmin" ||
    userRoleLower === "administrator";

  const getSubAccessStatus = (subItem) => {
    if (isAdminRole) return "FULL";

    if (rolePermissions && rolePermissions.length > 0) {
      const match = rolePermissions.find(
        (p) =>
          (p.routePath &&
            (p.routePath === `/my/${subItem.path}` ||
              p.routePath === subItem.path)) ||
          (p.code &&
            subItem.code &&
            p.code.toLowerCase() === subItem.code.toLowerCase()) ||
          (p.title &&
            subItem.title &&
            p.title.toLowerCase() === subItem.title.toLowerCase()),
      );

      if (match) {
        if (!match.canView) return "RESTRICTED";
        if (
          match.canView &&
          !match.canCreate &&
          !match.canEdit &&
          !match.canDelete
        )
          return "LIMITED";
        return "FULL";
      }
    }

    if (subItem.isReadOnly) return "LIMITED";
    return "FULL";
  };

  const hasSubMenu = item.subItems && item.subItems.length > 0;
  const allSubItems = hasSubMenu ? item.subItems : [];
  const showSubMenu = allSubItems.length > 0;

  const isChildActive =
    showSubMenu && allSubItems.some((sub) => currentPath === `/my/${sub.path}`);
  const isActive =
    !hasSubMenu && currentPath === `/my${item.path ? "/" + item.path : ""}`;

  useEffect(() => {
    if (isChildActive) {
      setIsOpen(true);
    }
  }, [isChildActive]);

  const handleClick = () => {
    if (showSubMenu) {
      setIsOpen(!isOpen);
    } else if (item.path !== undefined) {
      navigate(item.path);
      if (onCloseMobile) onCloseMobile();
    }
  };

  return (
    <div>
      <button
        onClick={handleClick}
        className={`relative flex items-center justify-between w-full px-3.5 py-2.5 text-xs font-extrabold rounded-2xl transition-all duration-200 group overflow-hidden ${
          isActive || (isChildActive && !isOpen)
            ? "bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-md shadow-sky-500/25"
            : "text-gray-600 dark:text-slate-300 hover:bg-sky-50/70 dark:hover:bg-slate-800/60 hover:text-sky-600 dark:hover:text-sky-400"
        }`}
      >
        <div className="flex items-center relative z-10">
          <span
            className={`text-base mr-3 transition-transform duration-200 group-hover:scale-110 ${
              isActive || (isChildActive && !isOpen)
                ? "text-white"
                : "text-gray-400 dark:text-slate-500 group-hover:text-sky-600 dark:group-hover:text-sky-400"
            }`}
          >
            {item.icon}
          </span>
          <span>{item.title}</span>
        </div>

        <div className="flex items-center gap-2">
          {item.badge && (
            <span className="text-[9px] font-black px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300 border border-sky-200 dark:border-sky-800">
              {item.badge}
            </span>
          )}

          {showSubMenu && (
            <motion.span
              animate={{ rotate: isOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              className={`text-[10px] relative z-10 transition-colors ${
                isActive || (isChildActive && !isOpen)
                  ? "text-white"
                  : "text-gray-400 dark:text-slate-500"
              }`}
            >
              <FaChevronDown />
            </motion.span>
          )}
        </div>
      </button>

      {/* Submenu List */}
      <AnimatePresence initial={false}>
        {showSubMenu && isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden pl-3 mt-1 space-y-1 border-l-2 border-sky-500/25 dark:border-slate-800 ml-4"
          >
            {item.subItems.map((subItem, idx) => {
              if (subItem.divider) {
                return (
                  <div
                    key={`div-${idx}`}
                    className="flex items-center gap-2 px-2 pt-2 pb-0.5"
                  >
                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 whitespace-nowrap">
                      {subItem.divider}
                    </span>
                    <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800" />
                  </div>
                );
              }
              const isSubActive = currentPath === `/my/${subItem.path}`;
              const access = getSubAccessStatus(subItem);
              const isRestricted = access === "RESTRICTED";
              const isLimited = access === "LIMITED";

              const handleSubClick = () => {
                if (isRestricted) {
                  toast.warning(
                    `Access Restricted: You do not have permission to access '${subItem.title}'. Please contact your administrator.`,
                  );
                  return;
                }
                navigate(subItem.path);
                if (onCloseMobile) onCloseMobile();
              };

              return (
                <button
                  key={idx}
                  onClick={handleSubClick}
                  className={`flex items-center justify-between w-full px-3 py-2 text-[11px] font-bold rounded-xl transition-all duration-200 ${
                    isSubActive
                      ? "bg-sky-50 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300 border border-sky-200/80 dark:border-sky-900/60"
                      : isRestricted
                        ? "text-gray-400 dark:text-slate-600 hover:bg-rose-50/40 dark:hover:bg-rose-950/20"
                        : "text-gray-500 dark:text-slate-400 hover:bg-sky-50/50 dark:hover:bg-slate-800/40 hover:text-sky-600 dark:hover:text-sky-400"
                  }`}
                  title={
                    isRestricted
                      ? `Access Restricted for ${subItem.title}`
                      : isLimited
                        ? `Read Only Access for ${subItem.title}`
                        : subItem.title
                  }
                >
                  <div className="flex items-center min-w-0">
                    {subItem.icon && (
                      <span
                        className={`mr-2.5 text-xs transition-transform duration-200 shrink-0 ${
                          isSubActive
                            ? "text-sky-600 dark:text-sky-400"
                            : isRestricted
                              ? "text-rose-400 dark:text-rose-600 opacity-70"
                              : "text-gray-400 dark:text-slate-500"
                        }`}
                      >
                        {subItem.icon}
                      </span>
                    )}
                    <span
                      className={`truncate ${isRestricted ? "opacity-80" : ""}`}
                    >
                      {subItem.title}
                    </span>
                  </div>

                  {/* Access Status Indicators */}
                  {isRestricted ? (
                    <span className="flex items-center gap-1 text-[8px] font-black text-rose-500 bg-rose-50 dark:bg-rose-950/80 border border-rose-200 dark:border-rose-900 px-1.5 py-0.5 rounded ml-2 shrink-0">
                      <FaLock size={8} /> Restricted
                    </span>
                  ) : isLimited ? (
                    <span className="flex items-center gap-1 text-[8px] font-black text-amber-600 bg-amber-50 dark:bg-amber-950/80 border border-amber-200 dark:border-amber-900 px-1.5 py-0.5 rounded ml-2 shrink-0">
                      <FaEye size={8} /> Read Only
                    </span>
                  ) : null}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
